import { Expression, Relation, Ty } from '@cotera/era';
import { EraQL } from '@cotera/era';
import { Result } from 'neverthrow';
import type { EventStreamDefinition } from '../../manifest';
import type { ToolSymbolMap } from '../../workflow/types';
import { DocumentEditor } from '../document';

// ============================================================================
// Tool Resolver Interface
// ============================================================================

/**
 * Information about a tool, returned by the resolver.
 */
export type ToolInfo = {
  readonly key: string;
  //llm compatible snake_case name
  readonly name: string;
  readonly inputTy: Ty.ExtendedAttributeType;
  readonly outputTy: Ty.ExtendedAttributeType;
};

/**
 * Entry for a tool, used for building the tool symbol map.
 * The `key` is typically the tool's unique identifier (e.g., "google/search").
 */
export type ToolEntry = {
  readonly key: string;
  readonly toolId: string;
  readonly toolName: string;
};

/**
 * Interface for tool resolution during graph building.
 * This abstracts away the source of tool information and allows
 * different implementations (e.g., CompositeToolLoader).
 *
 * Tools are now managed externally (in DB tables) rather than being
 * defined in the graph itself.
 */
export type IToolResolver = {
  /**
   * Check if a tool is registered by its key.
   *
   * @param toolKey - The tool's unique identifier (e.g., "google/search")
   * @returns true if the tool is registered (credential readiness is a runtime concern)
   */
  isToolAvailable: (toolKey: string) => boolean;

  /**
   * Get information about a tool by its key.
   *
   * @param toolKey - The tool's unique identifier (e.g., "google/search")
   * @returns Tool info if available, null otherwise
   */
  getToolInfo: (toolKey: string) => ToolInfo | null;
  list(): string[];
};

/**
 * Internal tool resolver for use ONLY in BuildExplorer.empty() and similar
 * cases where we know no tools will be resolved.
 *
 * DO NOT use this as a default - always pass a proper IToolResolver from
 * the tools-v2 system.
 *
 * @internal
 */
export const noToolsResolver: IToolResolver = {
  isToolAvailable(_toolKey) {
    return false;
  },
  getToolInfo(_toolKey) {
    return null;
  },
  list() {
    return [];
  },
};

export class AlwaysAllowToolResolver implements IToolResolver {
  isToolAvailable(_toolKey: string): boolean {
    return true;
  }
  getToolInfo(toolKey: string): ToolInfo | null {
    return {
      key: toolKey,
      name: toolKey,
      inputTy: Ty.ty('super'),
      outputTy: Ty.ty('super'),
    };
  }
  list(): string[] {
    return [];
  }
}

export class Identity {
  static wrap(x: Identity | [number, number]): Identity {
    return x instanceof Identity ? x : this.forOperation(x[0], x[1]);
  }

  private constructor(readonly commitIndex: number, readonly opIndex: number) {}

  eq(rhs: Identity): boolean {
    return this.toString() === rhs.toString();
  }

  static forOperation(commitIndex: number, opIndex: number): Identity {
    return new Identity(commitIndex, opIndex);
  }

  static fromString(str: string): Identity {
    const parsed = Identity.tryFromString(str);
    if (parsed === null) {
      throw new Error('Invalid identifier');
    }
    return parsed;
  }

  /**
   * Parses a data-graph commit identity (e.g. `commit:19op:12`).
   * Returns null for saved-agent UUIDs, symbol paths, and other non-commit strings.
   */
  static tryFromString(str: string): Identity | null {
    const [commitPart, opPart] = str.split('op:');
    if (commitPart === undefined || opPart === undefined || opPart === '') {
      return null;
    }
    if (!commitPart.startsWith('commit:')) {
      return null;
    }
    const commitIndex = parseInt(commitPart.slice('commit:'.length), 10);
    const opIndex = parseInt(opPart, 10);
    if (Number.isNaN(commitIndex) || Number.isNaN(opIndex)) {
      return null;
    }
    return new Identity(commitIndex, opIndex);
  }

  static fromTuple(tuple: [number, number]): Identity {
    const [commitIndex, opIndex] = tuple;
    return new this(commitIndex, opIndex);
  }

  asTuple(): [number, number] {
    return [this.commitIndex, this.opIndex];
  }

  commitString(): string {
    return `commit:${this.commitIndex}`;
  }

  opString(): string {
    return `op:${this.opIndex}`;
  }

  toString(): string {
    return `commit:${this.commitIndex}op:${this.opIndex}`;
  }

  compareTo(other: Identity): number {
    if (this.commitIndex !== other.commitIndex) {
      return this.commitIndex - other.commitIndex;
    }
    return this.opIndex - other.opIndex;
  }
}

export type Migration = never;

export const RESOLUTION_ORDERS = [
  'alphabetical',
  'reverse-alphabetical',
] as const;

export type ResolutionOrder = (typeof RESOLUTION_ORDERS)[number];

export const COMPARE_FUNCS: Record<
  ResolutionOrder,
  (left: string, right: string) => number
> = {
  alphabetical: (left: string, right: string) => left.localeCompare(right),
  'reverse-alphabetical': (left: string, right: string) =>
    right.localeCompare(left),
};

export type InterNodeLocation =
  | 'source'
  | 'pk'
  | 'where'
  | readonly ['col', string, InterColLocation];

export type InterColLocation =
  | 'source'
  | 'prompt'
  | 'systemMessage'
  | 'outputTy'
  | 'model'
  | 'temperature'
  | 'maxOutputTokens'
  | 'when'
  | 'imageUrl'
  | 'tools'
  | 'durable'
  | 'toolSymbolMap';

export type GraphDep = Pick<EraQL.TokenMeta, 'range'> & {
  readonly location: InterNodeLocation;
};

export type GraphDeps = {
  readonly [name: string]: readonly GraphDep[];
};

export type BuiltColNode = {
  readonly identity: Identity;
  readonly meta: ColMetadata;
  readonly eventStream: EventStreamDefinition | null;
};

export type RelItem = {
  readonly t: 'rel';
  readonly identity: Identity;
  readonly rel: Relation;
  readonly pk: Expression | null;
  readonly where: Expression | null;
  readonly relNode: RelNode;
  readonly cols: {
    readonly [name: string]: BuiltColNode;
  };
};

export type ExprItem = {
  readonly t: 'expr';
  readonly exprNode: ExprNode;
  readonly expr: Expression;
};

export type DocumentItem = {
  readonly t: 'document';
  readonly documentNode: DocumentNode;
};

export type ToolItemDef = {
  readonly t: 'ffi';
  readonly type: string; // match to our tool list
  readonly toolId: string;
  readonly toolConnectionId: string;
};

export type ToolItem = {
  readonly t: 'tool';
  readonly def: ToolItemDef;
};

export type AgentItem = {
  readonly t: 'agent';
  readonly agentNode: AgentNode;
  readonly availableToolSymbols: string[]; // tools available within an agent (derived from editorState)
  readonly editorState: DocumentEditor.DocumentState;
  readonly output: Ty.ExtendedAttributeType;
  readonly model: string;
  readonly schedule?: 'never' | 'daily' | 'hourly' | { cron: string };
};

export type Item = RelItem | ExprItem | DocumentItem | ToolItem | AgentItem;

export type Resolution = {
  readonly item: Item;
  readonly deps: GraphDeps;
};

export type EraQlColConfig = {
  readonly t: 'eraql';
  readonly source: string;
};

export type LlmColConfig = {
  readonly t: 'llm';
  readonly prompt: string;
  readonly systemMessage: string;
  readonly model: string;
  readonly temperature?: number;
  readonly topP?: number;
  readonly topK?: number;
  readonly frequencyPenalty?: number;
  readonly presencePenalty?: number;
  readonly maxOutputTokens?: number;
  readonly outputTy: string;
  readonly when: 'never' | 'daily' | 'hourly' | { cron: string };
  readonly imageUrl: string | null;
  readonly tools: string[];
  readonly durable?: boolean;
  readonly version?: 'v1';
};

export type LlmColConfigV2 = {
  readonly t: 'llm';
  readonly outputTy: string;
  readonly editorState: DocumentEditor.DocumentState;
  readonly model: string;
  readonly schedule?: 'never' | 'daily' | 'hourly' | { cron: string };
  readonly temperature?: number;
  readonly topP?: number;
  readonly topK?: number;
  readonly frequencyPenalty?: number;
  readonly presencePenalty?: number;
  readonly maxOutputTokens?: number;
  readonly version: 'v2';
};

export type WorkflowColConfig = {
  readonly t: 'workflow';
  readonly source: string;
  readonly toolSymbolMap: ToolSymbolMap;
  readonly when: 'never' | 'daily' | 'hourly' | { cron: string };
};

export type ColConfig =
  | EraQlColConfig
  | LlmColConfig
  | LlmColConfigV2
  | WorkflowColConfig;

// Make sure all keys of `RelCol` are in `InterColLocation` and vice versa
type KeysOfVariantObjects<T> = T extends any ? keyof T : never;
(key: InterColLocation): KeysOfVariantObjects<ColConfig> => key;
(
  key: Exclude<
    KeysOfVariantObjects<ColConfig>,
    | 't'
    | 'metadata'
    | 'topP'
    | 'topK'
    | 'frequencyPenalty'
    | 'presencePenalty'
    | 'version'
    | 'editorState'
    | 'schedule'
  >
): InterColLocation => key;

export type NodeMeta = {
  readonly identity: Identity;
  readonly isLocked: boolean;
  readonly editsAt: readonly Identity[];
  readonly predecessor?: Identity;
};

export type ExprNode = {
  readonly t: 'expr';
  readonly source: string;
  readonly meta: NodeMeta;
  readonly metadata?: Record<string, any>;
};

export type DocumentNode = {
  readonly t: 'document';
  readonly source: DocumentEditor.DocumentState;
  readonly meta: NodeMeta;
  readonly metadata?: Record<string, any>;
};

export type ToolNodeDef = {
  readonly t: 'ffi';
  readonly type: string;
  readonly toolId: string;
  readonly toolConnectionId: string;
};

export type ToolNode = {
  readonly t: 'tool';
  readonly def: ToolNodeDef;
  readonly meta: NodeMeta;
};

export type AgentNode = {
  readonly t: 'agent';
  readonly outputTy: string;
  readonly editorState: DocumentEditor.DocumentState;
  readonly model: string;
  readonly schedule?: 'never' | 'daily' | 'hourly' | { cron: string };
  readonly meta: NodeMeta;
};

export type ColNode = {
  readonly def: ColConfig;
  readonly meta: NodeMeta;
};

export type RelNode = {
  readonly t: 'rel';
  readonly source: string;
  readonly pk: string | null;
  readonly where: string | null;
  readonly meta: NodeMeta;
  readonly cols: {
    readonly [name: string]: ColNode;
  };
};

export type GraphNode =
  | ExprNode
  | DocumentNode
  | RelNode
  | ToolNode
  | AgentNode;

export type Apply =
  | {
      readonly t: 'expr';
      readonly expr: Expression;
    }
  | {
      readonly t: 'udd';
      readonly identifier: Expression;
      readonly key: string;
      readonly ty: Ty.ExtendedAttributeType;
    };

export type ColMetadata =
  | {
      readonly t: 'eraql';
      readonly expr: Expression;
    }
  | {
      readonly t: 'llm';
      readonly prompt: Expression;
    }
  | {
      readonly t: 'workflow';
    };

export type ColSuccess = {
  readonly identity: Identity;
  readonly apply: Apply;
  readonly deps: Record<string, readonly Pick<EraQL.TokenMeta, 'range'>[]>;
  readonly meta: ColMetadata;
  readonly eventStream: EventStreamDefinition | null;
};

export type ColFailure = [location: InterColLocation, err: GraphError];

export type ColResult = Result<ColSuccess, ColFailure>;

export type PartialSuccess = {
  readonly t: 'rel';
  readonly attributes: Record<string, Ty.ExtendedAttributeType>;
};

export class GraphErrorResult {
  constructor(
    readonly err: GraphError,
    readonly partial: PartialSuccess | null
  ) {}

  prettyPrint(): string {
    return prettyPrintGraphError(this.err);
  }
}

export type GraphResult = Result<Resolution, GraphErrorResult>;

export type GraphError =
  | {
      readonly t: 'cant-be-windowed' | 'cant-be-aggregated' | 'requires-pk';
    }
  | {
      readonly t: 'invalid-type';
      readonly got: Ty.ExtendedAttributeType;
      readonly wanted: Ty.ExtendedAttributeType[];
    }
  | { readonly t: 'aliasing-error'; readonly name: string }
  | {
      readonly t: 'missing-dependency';
      readonly name: string;
      readonly position: Pick<EraQL.TokenMeta, 'range'>;
    }
  | { readonly t: 'cyclical-dependency'; readonly stack: readonly string[] }
  | {
      readonly t: 'eraql-errors';
      readonly errs: readonly EraQL.WithRange<EraQL.ParseError>[];
      readonly source?: string; // Optional source string for context
    }
  | {
      readonly t: 'dep-has-error';
      readonly dep: string;
      readonly position: Pick<EraQL.TokenMeta, 'range'>;
    }
  | {
      readonly t: 'multi-error';
      readonly errs: readonly (readonly [
        location: InterNodeLocation,
        err: GraphError
      ])[];
    }
  | {
      readonly t: 'invalid-tool-type';
      readonly tool: string;
      readonly kind: 'input' | 'output';
      readonly inputIndex?: number;
    }
  | {
      readonly t: 'ffi-tool-not-found';
      readonly type: string;
      readonly key: string;
    };

/**
 * Extracts context around a position in a source string
 * @param source - The source string
 * @param position - The position to extract context around
 * @param contextChars - Number of characters to show before and after (default: 30)
 * @returns A formatted string with context and a pointer to the position
 */
const extractContext = (
  source: string,
  position: number,
  contextChars: number = 30
): string => {
  const start = Math.max(0, position - contextChars);
  const end = Math.min(source.length, position + contextChars);
  const before = source.slice(start, position);
  const at = source.slice(position, position + 1);
  const after = source.slice(position + 1, end);

  const pointerPos = before.length;
  const pointer = ' '.repeat(pointerPos) + '^';

  return `...${before}${at}${after}...\n${pointer}`;
};

/**
 * Pretty prints an InterNodeLocation in a human-readable format
 * @param location - The location to format
 * @returns A formatted string representation of the location
 */
export const prettyPrintLocation = (location: InterNodeLocation): string => {
  if (typeof location === 'string') {
    return location;
  }

  if (Array.isArray(location) && location[0] === 'col') {
    const [, colName, colLocation] = location;
    if (typeof colLocation === 'string') {
      return `col:${colName}->${colLocation}`;
    }
    return `col:${colName}->${colLocation}`;
  }

  return JSON.stringify(location);
};

/**
 * Safely checks if a value is a GraphError object
 */
const isGraphError = (value: unknown): value is GraphError => {
  return (
    value !== null &&
    typeof value === 'object' &&
    't' in value &&
    typeof (value as { t: unknown }).t === 'string'
  );
};

/**
 * Pretty prints a GraphError with human-readable messages
 * @param error - The graph error to format
 * @returns A formatted string describing the error
 */
export const prettyPrintGraphError = (error: GraphError | unknown): string => {
  // Handle non-GraphError values
  if (!isGraphError(error)) {
    if (typeof error === 'string') {
      return error;
    }
    return `Invalid error: ${JSON.stringify(error)}`;
  }

  switch (error.t) {
    case 'cant-be-windowed':
      return 'This operation cannot be windowed';

    case 'cant-be-aggregated':
      return 'This operation cannot be aggregated';

    case 'requires-pk':
      return 'This operation requires a primary key';

    case 'invalid-type':
      return `Invalid type: got '${
        error.got
      }', wanted one of: ${error.wanted.join(', ')}`;

    case 'aliasing-error':
      return `Aliasing error with name: ${error.name}`;

    case 'missing-dependency':
      return `Missing dependency: ${error.name} at position ${JSON.stringify(
        error.position
      )}`;

    case 'cyclical-dependency':
      return `Cyclical dependency detected in: ${error.stack.join(' → ')}`;

    case 'eraql-errors': {
      return `EraQL parsing errors (${error.errs.length}):\n${error.errs
        .map(([err, meta], index) => {
          const errorMsg = EraQL.prettyPrintParseError(err);
          const range = meta.range;

          // Handle range - it can be an array [start, end] or an object
          let rangeStart: number | null = null;
          let rangeEnd: number | null = null;

          if (Array.isArray(range) && range.length >= 2) {
            rangeStart = range[0];
            rangeEnd = range[1];
          }

          // Try to get source context if available
          let contextStr = '';
          if (error.source && rangeStart !== null) {
            const context = extractContext(error.source, rangeStart, 30);
            contextStr = `\n    Context:\n    ${context
              .split('\n')
              .join('\n    ')}`;
          }

          const rangeStr =
            rangeStart !== null && rangeEnd !== null
              ? `[${rangeStart},${rangeEnd}]`
              : JSON.stringify(range);

          return `  ${index + 1}. ${errorMsg} at ${rangeStr}${contextStr}`;
        })
        .join('\n')}`;
    }

    case 'dep-has-error':
      return `Dependency '${
        error.dep
      }' has an error at position ${JSON.stringify(error.position)}`;

    case 'multi-error': {
      if (!Array.isArray(error.errs)) {
        return `Multiple errors: Invalid error structure (errs is not an array)`;
      }
      return `Multiple errors (${error.errs.length}):\n${error.errs
        .map((item, index) => {
          // Handle tuple structure [location, err]
          if (Array.isArray(item) && item.length >= 2) {
            const [location, err] = item;
            const locationStr = prettyPrintLocation(location);
            return `  ${
              index + 1
            }. At ${locationStr}:\n    ${prettyPrintGraphError(err).replace(
              /\n/g,
              '\n    '
            )}`;
          }

          // Handle case where item is a GraphError object directly (not in a tuple)
          return `  ${index + 1}. ${prettyPrintGraphError(item)}`;
        })
        .join('\n')}`;
    }

    case 'invalid-tool-type':
      if (error.kind === 'input') {
        return `Invalid input type for tool '${error.tool}' at input index ${error.inputIndex}`;
      } else {
        return `Invalid output type for tool '${error.tool}'`;
      }

    case 'ffi-tool-not-found':
      return `FFI tool not found: ${error.type}`;

    default:
      return `Unknown graph error: ${JSON.stringify(error)}`;
  }
};
